import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import { aiService, type AiCredentialStatus } from '@services/aiService';
import { organizationService } from '@services/organizationService';
import AiOffNote from '../ai/AiOffNote';
import { apiErrorCode, toDisplayError } from '../../utils/apiError';

interface AiProviderSectionProps {
  orgId: string;
  /** Admin+. Members see the status but get no controls. */
  canManage: boolean;
}

const EMPTY: AiCredentialStatus = {
  configured: false,
  provider: null,
  keyLast4: null,
  lastUsedAt: null,
  updatedAt: null,
  // REV-1127: a probe that tells us nothing does not claim AI is off.
  aiEnabled: true,
};

/**
 * REV-513: the organization's own LLM key.
 *
 * Sits below the issue-tracker catalog rather than inside it. "Connect an AI
 * provider" is what a user comes to this page looking for, but it is not an
 * issue tracker — the catalog's entries all mean "push a finding somewhere",
 * and folding a credential for a different kind of service into that list
 * would conflate two things the API deliberately keeps apart.
 *
 * The key is write-only end to end: it is never fetched, never rendered, and
 * the input is cleared the moment it is stored. All this component can ever
 * display is the last four characters the API reports back.
 */
export default function AiProviderSection({ orgId, canManage }: AiProviderSectionProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<AiCredentialStatus>(EMPTY);
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      // REV-604: `?? EMPTY` is not defensive clutter — without it this line
      // could take the whole page down. A response that resolves with no usable
      // body put `undefined` into state, and the next render threw on
      // `status.configured`, which unmounted the React tree and left the body
      // empty. Every assertion in IntegrationsPage.test.tsx then failed with
      // "Unable to find role=dialog", pointing at the wrong thing entirely.
      //
      // The catch below already treats a failed probe as "not configured". A
      // probe that answers with nothing usable has told us exactly as much, so
      // it gets the same answer. This is a status probe for an optional
      // feature; nothing it returns should be able to break the page it is on.
      setStatus((await aiService.getCredentialStatus(orgId)) ?? EMPTY);
    } catch {
      // Treated as "not configured" — the controls below still work, and an
      // error banner for a status probe would be noise.
      setStatus(EMPTY);
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async () => {
    // Validate on submit, never by disabling the button: a disabled control
    // cannot tell you what is missing (platform form rule).
    if (!apiKey.trim()) {
      setError(t('integrations.ai.errors.required', 'Paste your API key first.'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // The saved-key response says nothing about the AI switch; keep it.
      const saved = await aiService.setCredential(orgId, apiKey.trim());
      setStatus((prev) => ({ ...prev, ...saved, aiEnabled: prev.aiEnabled }));
      // Do not leave a live credential sitting in a form field.
      setApiKey('');
    } catch (err) {
      // REV-694: anything else is the API's own reason ("That does not look
      // like a valid API key."), which tells the admin what to fix; the
      // generic line did not. The API sends only its own words (REV-770).
      setError(
        apiErrorCode(err) === 'encryption_unavailable'
          ? t(
              'integrations.ai.errors.noEncryption',
              'This deployment cannot store an API key securely yet, so it was not saved. Ask your administrator to configure at-rest encryption.'
            )
          : toDisplayError(
              err,
              t('integrations.ai.errors.saveFailed', 'The key could not be saved.')
            )
      );
    } finally {
      setSaving(false);
    }
  }, [apiKey, orgId, t]);

  const remove = useCallback(async () => {
    setRemoving(true);
    setError(null);
    try {
      await aiService.deleteCredential(orgId);
      setStatus((prev) => ({ ...EMPTY, aiEnabled: prev.aiEnabled }));
    } catch {
      setError(t('integrations.ai.errors.removeFailed', 'The key could not be removed.'));
    } finally {
      setRemoving(false);
    }
  }, [orgId, t]);

  // REV-1127: only an explicit false is off, as EMPTY and AiFixSuggestion
  // read it. A status without the field (an API older than the switch) has
  // told us nothing, and must not claim AI is off.
  const aiOn = status.aiEnabled !== false;

  /**
   * REV-1127: the organization AI switch. Saved on change, as a switch is.
   * While a change is in flight the switch is aria-disabled, not disabled:
   * Chromium moves focus to the body when the focused control becomes
   * disabled, so a keyboard user who pressed Space lost their place and their
   * next Space did nothing. The ref, not the state, refuses a second change
   * meanwhile, because a change can arrive before the re-render.
   */
  const inFlight = useRef(false);
  const toggleAi = useCallback(
    async (enabled: boolean) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setSwitching(true);
      setError(null);
      try {
        const org = await organizationService.update(orgId, { ai_enabled: enabled });
        setStatus((prev) => ({ ...prev, aiEnabled: org?.ai_enabled ?? enabled }));
      } catch (err) {
        setError(
          toDisplayError(
            err,
            t('integrations.ai.errors.switchFailed', 'The AI setting could not be changed.')
          )
        );
      } finally {
        inFlight.current = false;
        setSwitching(false);
      }
    },
    [orgId, t]
  );

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <AutoAwesomeIcon fontSize="small" aria-hidden="true" />
        <Typography variant="h6" component="h2">
          {t('integrations.ai.title', 'AI provider')}
        </Typography>
        <Chip
          size="small"
          color={status.configured ? 'success' : 'default'}
          label={
            status.configured
              ? t('integrations.ai.connectedAs', 'Connected · key ending {{last4}}', {
                  last4: status.keyLast4 ?? '····',
                })
              : t('integrations.ai.notConnected', 'Not connected')
          }
        />
      </Box>

      <Typography variant="body2" color="text.secondary">
        {t(
          'integrations.ai.blurb',
          'Add your own Anthropic API key to turn on AI fix suggestions. Requests are billed to your account by your provider, not by us, and every call is recorded in your organization’s audit trail.'
        )}
      </Typography>

      {canManage && (
        <FormControlLabel
          sx={{ mt: 2, display: 'flex' }}
          control={
            <Switch
              checked={aiOn}
              onChange={(e) => void toggleAi(e.target.checked)}
              // The switch pattern: announced as on or off, not as checked.
              // aria-disabled, not disabled, while saving (see toggleAi).
              slotProps={{ input: { role: 'switch', 'aria-disabled': switching || undefined } }}
            />
          }
          label={t('integrations.ai.allowLabel', 'Allow AI features in this organization')}
        />
      )}
      {!aiOn && <AiOffNote sx={{ mt: 2 }} />}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {canManage && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <TextField
            label={t('integrations.ai.keyLabel', 'API key')}
            // The API never returns the key, so this is only ever a
            // write-path. It is masked and cleared on success.
            type="password"
            value={apiKey}
            onChange={(e) => {
              setApiKey(e.target.value);
              if (error) setError(null);
            }}
            fullWidth
            autoComplete="off"
            helperText={
              status.configured
                ? t('integrations.ai.replaceHelp', 'Saving a new key replaces the existing one.')
                : t('integrations.ai.keyHelp', 'Stored encrypted. It is never shown again.')
            }
            inputProps={{ spellCheck: 'false' }}
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={save}
              // Disabled only in flight, never as a validation gate.
              disabled={saving}
              aria-busy={saving}
              startIcon={
                saving ? <CircularProgress size={18} color="inherit" aria-hidden="true" /> : null
              }
            >
              {status.configured
                ? t('integrations.ai.replace', 'Save new key')
                : t('integrations.ai.save', 'Save key')}
            </Button>
            {/* outlined, not the MUI default text variant: as bare text this
                reads as a paragraph rather than a control (CSS-16, caught by
                our own gate) — bad for any button, worse for a destructive
                one. */}
            {status.configured && (
              <Button
                variant="outlined"
                color="error"
                onClick={remove}
                disabled={removing}
                aria-busy={removing}
              >
                {t('integrations.ai.remove', 'Remove key')}
              </Button>
            )}
          </Box>
        </Stack>
      )}
    </Paper>
  );
}
